import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { useDriverDevices, type DeviceAction } from '../hooks/use-driver-devices';
import { deviceName } from '../policies/device-event-labels';
import { CurrentDeviceCard } from './CurrentDeviceCard';
import { DeviceHistoryTable } from './DeviceHistoryTable';
import { ReasonModal } from './ReasonModal';

const MODAL_COPY: Record<DeviceAction, { title: string; description: string; confirmLabel: string }> = {
  revoke: {
    title: 'Revoke this phone',
    description: 'The driver is signed out at once and has to sign in again. Anything not yet synced stays on the phone.',
    confirmLabel: 'Revoke',
  },
  wipe: {
    title: 'Wipe this phone',
    description: 'The app clears its data the next time it contacts the server. The driver has to sign in again afterwards.',
    confirmLabel: 'Request wipe',
  },
};

/** The driver's phone and its history: look at it, revoke it, or have the app wipe itself. Needs `driver.device.manage`. */
export function DriverDevicesTab({ driverId }: { driverId: string }) {
  const { state, list, act } = useDriverDevices(driverId);
  const [pending, setPending] = useState<DeviceAction | null>(null);

  if (state.status === 'loading') return <TableSkeleton label="Loading devices" />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load devices" description="Something went wrong. Try reloading the page." />;
  }

  const { current, history } = state.devices;
  const copy = pending ? MODAL_COPY[pending] : MODAL_COPY.revoke;

  return (
    <div className="flex flex-col gap-6">
      <CurrentDeviceCard device={current} onAct={setPending} />
      {history.total > 0 && <DeviceHistoryTable history={history} list={list} />}
      <ReasonModal
        open={pending !== null && current !== null}
        {...copy}
        title={current ? `${copy.title}: ${deviceName(current)}` : copy.title}
        onCancel={() => setPending(null)}
        onConfirm={async (reason) => {
          if (!pending || !current) return false;
          const done = await act(pending, current, reason);
          if (done) setPending(null);
          return done;
        }}
      />
    </div>
  );
}
